// src/components/WithdrawRewards.js
import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { getContract } from "../utils";
import Modal from "./Modal";

export default function WithdrawRewards({ wallet, fetchAllPools, fetchUnlockedLevel }) {
  // Levels info (UI labels, investments, etc.)
  const LEVELS = {
    0: { name: "Entry", max: 3, investment: 16, totalReward: 43, background: "bg-blue-100", image: "/images/entry.png" },
    1: { name: "Stacker", max: 4, investment: 32, totalReward: 115, background: "bg-green-100", image: "/images/stacker.png" },
    2: { name: "Scaler", max: 5, investment: 64, totalReward: 288, background: "bg-yellow-100", image: "/images/scaler.png" },
    3: { name: "Flipper", max: 6, investment: 128, totalReward: 691, background: "bg-orange-100", image: "/images/flipper.png" },
    4: { name: "Holder", max: 5, investment: 256, totalReward: 1152, background: "bg-purple-100", image: "/images/holder.png" },
    5: { name: "Broker", max: 4, investment: 512, totalReward: 1843, background: "bg-red-100", image: "/images/broker.png" },
    6: { name: "Tycoon", max: 3, investment: 1024, totalReward: 2765, background: "bg-pink-100", image: "/images/tycoon.png" },
    7: { name: "Legend", max: 10, investment: 2048, totalReward: 18432, background: "bg-indigo-100", image: "/images/legend.png" }
  };

  // Alert modal state and helper
  const [alertModal, setAlertModal] = useState({ show: false, title: "", message: "" });
  const showAlert = (title, message) => setAlertModal({ show: true, title, message });

  // Reward state
  const [totalEarned, setTotalEarned] = useState("0");
  const [claimableRewards, setClaimableRewards] = useState("0");
  const [pendingCommissions, setPendingCommissions] = useState("0");

  // Eligible pools and dropdown state
  const [eligiblePools, setEligiblePools] = useState([]);
  const [selectedEligiblePoolId, setSelectedEligiblePoolId] = useState(null);
  const [nextLevel, setNextLevel] = useState(null);
  const [availableTargetPools, setAvailableTargetPools] = useState([]);
  const [selectedTargetPoolId, setSelectedTargetPoolId] = useState(null);
  const [selectedOption, setSelectedOption] = useState("join");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allPools, setAllPools] = useState([]);

  // Enable claim rewards button only if some eligible pools exist and there's a claimable reward
  const canClaim = eligiblePools.length > 0 && parseFloat(claimableRewards) > 0;

  // Use globalId for display purposes and for passing to contract calls.
  // (We assign globalId as the array index + 1 when mapping the pools from the contract.)
  const selectedEligiblePoolObj = eligiblePools.find(
    (pool) => pool.globalId?.toString() === selectedEligiblePoolId?.toString()
  );

  // Calculate the "individual reward" based on claimableRewards divided by the number of eligible pools.
  const individualReward =
    eligiblePools.length > 0
      ? (parseFloat(claimableRewards) / eligiblePools.length).toFixed(18)
      : claimableRewards;

  // Fetch rewards and eligible pools from the contract
  const fetchRewards = async () => {
    try {
      const contract = getContract(wallet.signer);
      const userAddress = (await wallet.signer.getAddress()).toLowerCase();

      // Fetch totals
      const totalEarnedRaw = await contract.getUserTotalEarned();
      setTotalEarned(ethers.formatUnits(totalEarnedRaw, 18));

      const claimableRaw = await contract.calculatePendingReward(userAddress);
      setClaimableRewards(ethers.formatUnits(claimableRaw, 18));

      const commissionsRaw = await contract.getUserBalance();
      setPendingCommissions(ethers.formatUnits(commissionsRaw, 18));

      const allPoolsRaw = await contract.getAllPools();
      const allPoolsData = allPoolsRaw.map((b, index) => ({
        globalId: BigInt(index + 1),
        localId: b.id.toString(),
        level: Number(b.level),
        center: b.center,
        status: Number(b.status),
        rewardClaimed: b.rewardClaimed,
        participants: b.participants
      }));
      setAllPools(allPoolsData);

      const eligible = allPoolsData.filter((pool) => {
        const isOwned = pool.center.toLowerCase() === userAddress;
        return (
          isOwned &&
          pool.status === 1 &&
          pool.participants.length === LEVELS[pool.level].max &&
          !pool.rewardClaimed
        );
      });

      setEligiblePools(eligible);

      if (eligible.length > 0) {
        setSelectedEligiblePoolId(eligible[0].globalId);
        const currentLevel = Number(eligible[0].level);
        const nextLvl = currentLevel + 1;
        setNextLevel(nextLvl);
        if (selectedOption === "join") {
          const available = await contract.getAvailablePoolsByLevel(nextLvl);
          setAvailableTargetPools(available);
          if (available.length > 0) setSelectedTargetPoolId(available[0]);
        }
      }
    } catch (err) {
      console.error("Error fetching rewards/eligible pools:", err);
    }
  };

  useEffect(() => {
    fetchRewards();
    const interval = setInterval(fetchRewards, 10000);
    return () => clearInterval(interval);
  }, [selectedOption, wallet]);

  // When the user changes the dropdown selection, update the selected eligible pool id.
  const handleEligiblePoolChange = async (e) => {
    const poolId = e.target.value;
    setSelectedEligiblePoolId(poolId);
    const eligiblePool = eligiblePools.find((b) => b.globalId.toString() === poolId);
    if (eligiblePool) {
      const currentLevel = Number(eligiblePool.level);
      const nextLvl = currentLevel + 1;
      setNextLevel(nextLvl);
      if (selectedOption === "join") {
        const contract = getContract(wallet.signer);
        const available = await contract.getAvailablePoolsByLevel(nextLvl);
        setAvailableTargetPools(available);
        if (available.length > 0) setSelectedTargetPoolId(available[0]);
      }
    }
  };

  // When the user changes the option (join, create, or withdraw)
  const handleOptionChange = async (e) => {
    const option = e.target.value;
    setSelectedOption(option);
    if (option === "join" && nextLevel != null) {
      const contract = getContract(wallet.signer);
      const available = await contract.getAvailablePoolsByLevel(nextLevel);
      setAvailableTargetPools(available);
      if (available.length > 0) setSelectedTargetPoolId(available[0]);
    }
  };

  // The function that is triggered when the user clicks "Confirm Action"
  const handleWithdraw = async () => {
    console.log("🚨 Confirm Action triggered!", {
      selectedOption,
      selectedEligiblePoolId,
      selectedTargetPoolId
    });

    // If in 'join' mode with no target pool available, we treat it as a "create" option
    if (selectedOption === "join" && availableTargetPools.length === 0) {
      setSelectedOption("create");
      try {
        setLoading(true);
        const contract = getContract(wallet.signer);
        if (!selectedEligiblePoolId) {
          showAlert("Error", "No eligible pool selected to claim from.");
          return;
        }

        const tx = await contract.withdrawReward(
          BigInt(selectedEligiblePoolId),
          2,
          0
        );
        const receipt = await tx.wait();

        let successMessage = "";
        const poolCreatedEvent = receipt.events && receipt.events.find(
          (event) => event.event === "PoolCreated"
        );
        if (poolCreatedEvent) {
          const globalPoolId = poolCreatedEvent.args.globalPoolId;
          const poolLevel = poolCreatedEvent.args.level;
          const createdPoolName = LEVELS[poolLevel] ? LEVELS[poolLevel].name : `Level ${poolLevel}`;
          successMessage += `New ${createdPoolName} Pool created with ID #${globalPoolId}. `;
        }
        const rewardEvent = receipt.events && receipt.events.find(
          (event) => event.event === "RewardClaimed"
        );
        if (rewardEvent) {
          const rewardAmount = ethers.formatUnits(rewardEvent.args.amount, 18);
          successMessage += `You received ${rewardAmount} USDT.`;
        }
        if (!successMessage) {
          successMessage = `Reward withdrawal successful (new ${LEVELS[nextLevel] ? LEVELS[nextLevel].name : "pool"} created)!`;
        }
        showAlert("Success", successMessage);
        setIsModalOpen(false);
        await fetchRewards();
        await fetchAllPools();
        await fetchUnlockedLevel();
      } catch (err) {
        console.error("Withdrawal error:", err);
        const errorMessage =
          err?.reason ||
          err?.error?.message ||
          err?.message ||
          "An unknown error occurred.";
        showAlert("Withdrawal failed", errorMessage);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Otherwise, proceed normally
    if (!selectedEligiblePoolId) {
      showAlert("Error", "Select an eligible pool first.");
      return;
    }

    try {
      setLoading(true);
      const contract = getContract(wallet.signer);
      // Find selected pool for logging purposes
      const selectedPool = eligiblePools.find(
        (b) => b.globalId.toString() === selectedEligiblePoolId.toString()
      );

      let tx;
      if (selectedOption === "join") {
        if (!selectedTargetPoolId) {
          showAlert("Error", "Please select a target pool to join at the next level.");
          setLoading(false);
          return;
        }
        tx = await contract.withdrawReward(BigInt(selectedEligiblePoolId), 1, BigInt(selectedTargetPoolId));
      } else if (selectedOption === "create") {
        tx = await contract.withdrawReward(BigInt(selectedEligiblePoolId), 2, 0);
      } else if (selectedOption === "withdraw") {
        tx = await contract.withdrawReward(BigInt(selectedEligiblePoolId), 3, 0);
      }
      const receipt = await tx.wait();

      let successMessage = "";
      const poolCreatedEvent = receipt.events && receipt.events.find(
        (event) => event.event === "PoolCreated"
      );
      if (poolCreatedEvent) {
        const globalPoolId = poolCreatedEvent.args.globalPoolId;
        const poolLevel = poolCreatedEvent.args.level;
        const createdPoolName = LEVELS[poolLevel] ? LEVELS[poolLevel].name : `Level ${poolLevel}`;
        successMessage += `New ${createdPoolName} Pool created with ID #${globalPoolId}. `;
      }
      const rewardEvent = receipt.events && receipt.events.find(
        (event) => event.event === "RewardClaimed"
      );
      if (rewardEvent) {
        const rewardAmount = ethers.formatUnits(rewardEvent.args.amount, 18);
        successMessage += `You received ${rewardAmount} USDT.`;
      }
      if (!successMessage) {
        successMessage = "Reward withdrawal successful!";
      }
      showAlert("Success", successMessage);
      setIsModalOpen(false);
      await fetchRewards();
      await fetchAllPools();
      await fetchUnlockedLevel();
    } catch (err) {
      console.error("Withdrawal error:", err);
      const errorMessage =
        err?.reason ||
        err?.error?.message ||
        err?.message ||
        "An unknown error occurred.";
      showAlert("Withdrawal failed", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="withdraw-container">
      <h2 className="withdraw-header">Withdraw Rewards</h2>

      <p className="withdraw-info">
        <strong>Claimable Rewards:</strong> {claimableRewards} USDT
      </p>
      <p className="withdraw-info">
        <strong>Total Earned:</strong> {totalEarned} USDT
      </p>
      {/* <p className="withdraw-info">
        <strong>Pending Commissions:</strong> {pendingCommissions} USDT
      </p> */}

      <button
        onClick={() => setIsModalOpen(true)}
        className="btn-claim"
        disabled={!canClaim}
      >
        {canClaim ? "Claim Rewards" : "No Rewards to Claim"}
      </button>

      <p className="withdraw-message">
        Only creator of a full pool can withdraw rewards
      </p>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <h3 className="modal-title">Claim & Reinvest</h3>

        {/* — Pool selector — */}
        {eligiblePools.length > 0 ? (
          <div className="modal-section">
            <label className="modal-label">Select Pool To Claim Rewards:</label>
            <select
              value={selectedEligiblePoolId || ''}
              onChange={handleEligiblePoolChange}
              className="modal-select"
            >
              {eligiblePools.map((pool) => (
                <option
                  key={`pool-${pool.globalId}-${pool.level}`}
                  value={pool.globalId.toString()}
                >
                  {`${LEVELS[Number(pool.level)]?.name || `Level ${Number(pool.level)}`} Pool #${pool.globalId}`}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="modal-error">
            No eligible pools available for withdrawal.
          </p>
        )}

        {/* — Action chooser — */}
        <div className="modal-section">
          <label className="modal-label">Select Action:</label>
          <div className="modal-radio-group">
            <label>
              <input
                type="radio"
                value="join"
                checked={selectedOption === "join"}
                onChange={handleOptionChange}
              />
              Join
            </label>
            <label>
              <input
                type="radio"
                value="create"
                checked={selectedOption === "create"}
                onChange={handleOptionChange}
              />
              Create
            </label>
            <label>
              <input
                type="radio"
                value="withdraw"
                checked={selectedOption === "withdraw"}
                onChange={handleOptionChange}
              />
              Withdraw
            </label>
          </div>
        </div>

        {/* — JOIN CONFIRMATION — */}
        {selectedOption === "join" &&
          nextLevel != null &&
          selectedTargetPoolId && (
            <>
              <div className="modal-section">
                <label className="modal-label">
                  Select Target Pool {LEVELS[nextLevel]?.name || `Level ${nextLevel}`}:
                </label>
                {availableTargetPools.length > 0 ? (
                  <select
                    value={selectedTargetPoolId}
                    onChange={e => setSelectedTargetPoolId(e.target.value)}
                    className="modal-select"
                  >
                    {Array.from(availableTargetPools)
                      .slice()
                      .sort((a, b) => Number(a) - Number(b))
                      .map(globalId => {
                        const pool = allPools.find(
                          p => p.globalId.toString() === globalId.toString()
                        );
                        const localId = pool ? pool.localId : globalId.toString();

                        return (
                          <option key={globalId.toString()} value={globalId.toString()}>
                            {`${LEVELS[nextLevel].name} Pool #${localId}`}
                          </option>
                        );
                      })
                    }
                  </select>
                ) : (
                  <p className="no-target-pool-message">
                    No Pool {LEVELS[nextLevel]?.name || `Level ${nextLevel}`} available.
                  </p>
                )}
              </div>

              {(() => {
                const investAmt = LEVELS[nextLevel].investment;
                const rewardAmt = parseFloat(individualReward ?? 0);
                const withdrawAmt = Math.max(0, rewardAmt - investAmt).toFixed(2);
                const poolName = LEVELS[nextLevel].name;
                return (
                  <div className="modal-confirmation">
                    <p>
                      Join a <strong>{poolName}</strong> pool: invest{' '}
                      <strong>{investAmt.toFixed(2)} USDT</strong>, withdraw{' '}
                      <strong>{withdrawAmt} USDT</strong>, new <strong>{poolName}</strong> pool will be created.
                    </p>

                  </div>
                );
              })()}
            </>
          )}
        {/* — CREATE CONFIRMATION — */}
        {selectedOption === "create" && nextLevel != null && (
          <>
            <div className="modal-section">
              <label className="modal-label">
                Create Pool {LEVELS[nextLevel]?.name || `Level ${nextLevel}`}
              </label>
              <p>
                Cost: {LEVELS[nextLevel].investment.toFixed(2)} USDT | Reward:{' '}
                {parseFloat(individualReward).toFixed(2)} USDT
              </p>
            </div>

            {(() => {
              const investAmt = LEVELS[nextLevel].investment;
              const rewardAmt = parseFloat(individualReward);
              const withdrawAmt = Math.max(0, rewardAmt - investAmt).toFixed(2);
              const poolName = LEVELS[nextLevel].name;
              return (
                <div className="modal-confirmation">
                  <p>
                    Create a <strong>{poolName}</strong> pool: invest <strong>{investAmt.toFixed(2)} USDT</strong>, withdraw <strong>{withdrawAmt} USDT</strong>.
                  </p>
                </div>
              );
            })()}
          </>
        )}
        {/* — WITHDRAW CONFIRMATION — */}
        {selectedOption === "withdraw" && selectedEligiblePoolObj && (
          <>
            <div className="modal-section">
              <label className="modal-label">
                Claim Rewards from Pool #{selectedEligiblePoolObj.localId}{' '}
                ({LEVELS[selectedEligiblePoolObj.level]?.name})
              </label>
              <p>
                {parseFloat(
                  selectedEligiblePoolObj.pendingReward ?? individualReward
                ).toFixed(2)}{' '}
                USDT
              </p>
            </div>
            {(() => {
              const payout = parseFloat(
                selectedEligiblePoolObj.pendingReward ?? individualReward
              ).toFixed(2);
              const lvlName = LEVELS[selectedEligiblePoolObj.level].name;
              return (
                <div className="modal-confirmation">
                  <p>
                    When withdrawing you will receive <strong>{payout} USDT</strong>, but keep your
                    current level (<strong>{lvlName}</strong>).
                  </p>
                </div>
              );
            })()}
          </>
        )}

        <button
          onClick={handleWithdraw}
          disabled={loading || !selectedEligiblePoolId}
          className="btn-confirm"
        >
          {loading
            ? 'Processing...'
            : selectedOption === 'join' && availableTargetPools.length === 0
              ? 'Create New Pool'
              : 'Confirm Action'}
        </button>
      </Modal>
      {alertModal.show && (
        <Modal
          isOpen={alertModal.show}
          onClose={() => setAlertModal({ show: false, title: "", message: "" })}
        >
          <div className="p-4">
            <h3 className="font-bold text-xl mb-2">{alertModal.title}</h3>
            <p className="mb-4 whitespace-pre-wrap">{alertModal.message}</p>
            <button className="btn-primary px-4 py-2" onClick={() => setAlertModal({ show: false, title: "", message: "" })}>
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
